import { sumDecimals } from '@cpce/shared';
import type Decimal from 'decimal.js';
import { useMemo, useRef } from 'react';
import { Table } from '@/components/table/Table';
import { useTableState } from '@/components/table/useTableState';
import { formatDecimalAsPrice } from '@/utils/string.utils';
import {
  createPaymentInstalmentColumns,
  type PaymentInstalmentRow,
} from '../columns/payment-instalments-columns';

interface PaymentScheduleTableProps {
  rows: PaymentInstalmentRow[];
  hasInstrumentNumber: boolean;
  /** Set while the échéancier is being built: the total shows red until it matches. */
  totalDue?: Decimal;
  onUpdateRow?: (id: string, row: PaymentInstalmentRow) => void;
}

export function PaymentScheduleTable(props: PaymentScheduleTableProps) {
  const { rows, hasInstrumentNumber, totalDue, onUpdateRow } = props;

  // Refs keep the column definitions stable across keystrokes: rebuilding them would remount the
  // cells and steal the focus from the input being typed in.
  const onUpdateRowRef = useRef(onUpdateRow);
  onUpdateRowRef.current = onUpdateRow;
  const isEditable = !!onUpdateRow;

  const columns = useMemo(
    () =>
      createPaymentInstalmentColumns({
        update: isEditable ? (id, row) => onUpdateRowRef.current?.(id, row) : undefined,
        hasInstrumentNumber,
      }),
    [hasInstrumentNumber, isEditable]
  );

  const tableState = useTableState('payment-schedule', { columns, persist: false });

  // Summed from the échéancier itself, never from the table's filtered rows: hiding a line must not
  // make the total look right.
  const total = sumDecimals(rows.map((row) => row.amount));
  const isShort = totalDue !== undefined && !total.equals(totalDue);

  return (
    <Table
      entity={{ singular: 'échéance', feminine: true }}
      data={rows}
      columns={columns}
      tableState={tableState}
      footer={
        <tfoot className="border-t-2 border-gray-200 bg-gray-50">
          <tr>
            <td colSpan={hasInstrumentNumber ? 2 : 1} className="p-4 text-right font-bold">
              Total échéancier
            </td>
            <td className={`p-4 font-bold ${isShort ? 'text-red-600' : ''}`}>
              {formatDecimalAsPrice(total)}
            </td>
          </tr>
        </tfoot>
      }
    />
  );
}
